import { useCallback, useState } from 'react'
import type { PrimaryWorkAction } from './workActionPolicy'
import { nowInstant } from './workSessionTime'

/** The actions that are a pure tap. The two closing actions open a sheet instead. */
export type TapAction = Extract<PrimaryWorkAction, 'start-travel' | 'start'>

interface Runners {
  'start-travel'?: () => Promise<unknown>
  start: () => Promise<unknown>
  /** `Rückgängig` — clears the last stamped instant. Omit where undoing is not offered. */
  undo?: () => Promise<unknown>
}

export interface PhaseTaps {
  run: (action: TapAction) => void
  undo: (() => void) | undefined
  isPending: boolean
  /** The last tap did not reach the server. The action bar must keep saying so. */
  failed: boolean
  /** Set when a tap landed, so the bar can confirm it and offer `Rückgängig`. */
  confirmedAt: string | null
}

/**
 * The tap half of the worker flow: fire the mutation, then own the three outcomes the action bar
 * renders — in flight, landed (with an undo window), or failed.
 *
 * The failure state is the load-bearing one. The worker has no backfill: a tap that silently
 * fails leaves him believing the session is open, and only the office can repair that. So a
 * failure is sticky until a retry succeeds, and never auto-dismisses.
 */
export function usePhaseTaps(runners: Runners): PhaseTaps {
  const [isPending, setIsPending] = useState(false)
  const [failed, setFailed] = useState(false)
  const [confirmedAt, setConfirmedAt] = useState<string | null>(null)

  const execute = useCallback(async (run: () => Promise<unknown>) => {
    setIsPending(true)
    setFailed(false)
    try {
      await run()
      setConfirmedAt(nowInstant())
    } catch {
      // The message itself belongs to the page's error mapping; the bar only needs to know that
      // this did not land.
      setFailed(true)
    } finally {
      setIsPending(false)
    }
  }, [])

  const run = useCallback(
    (action: TapAction) => {
      const runner = runners[action]
      if (!runner) return
      void execute(runner)
    },
    // The runners are fresh closures each render; only the executor identity matters here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [execute],
  )

  const undo = useCallback(() => {
    if (!runners.undo) return
    setConfirmedAt(null)
    void execute(runners.undo)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [execute])

  return {
    run,
    undo: runners.undo ? undo : undefined,
    isPending,
    failed,
    confirmedAt,
  }
}
